/*
 * Buy Now + Add-ons bridge.
 *
 * The "BUY NOW" button is Shopify's `{{ form | payment_button }}` driven by
 * Razorpay Magic Checkout (one-click). It checks out only the product form's
 * single line item and never runs the Add-to-Cart add-on JS, so selected
 * add-ons (which are separate cart line items) are dropped.
 *
 * We cannot reliably intercept the click on Shopify's own buy-it-now button:
 * it registers a document-level capture listener with stopImmediatePropagation
 * at page load, so a normal click handler is a load-order race.
 *
 * Instead, when (and only when) add-ons are selected, we show a transparent
 * overlay ON TOP of the button inside `.rzp-buynow-skin`. The overlay is a
 * plain element (not Shopify's button), so Shopify ignores clicks on it and
 * our handler runs deterministically. It adds the main product + each add-on
 * to the cart, then opens Razorpay Magic Checkout against the cart (so every
 * line item, including add-ons, is honoured). With no add-ons selected the
 * overlay is hidden and the native instant one-click flow is untouched.
 */
(function () {
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function num(v) {
    var n = parseInt(String(v == null ? '' : v).trim(), 10);
    return isNaN(n) ? 0 : n;
  }

  function selectedAddons(productId) {
    var sel = productId
      ? '.product-addons__checkbox[data-product-id="' + productId + '"]:checked'
      : '.product-addons__checkbox:checked';
    return Array.prototype.slice.call(document.querySelectorAll(sel))
      .map(function (c) {
        return { id: num(c.dataset.addonVariant), quantity: num(c.dataset.addonQuantity) || 1 };
      })
      .filter(function (a) { return a.id; });
  }

  function predrapeVariant(productId) {
    var w = document.querySelector('[data-predrape-addon]' + (productId ? '[data-product-id="' + productId + '"]' : ''));
    if (!w) return null;
    var sel = w.querySelector('[data-predrape-select]');
    return sel && sel.value ? sel.value : null;
  }

  function blouseData(productId) {
    var w = document.querySelector('[data-blouse-addon]' + (productId ? '[data-product-id="' + productId + '"]' : ''));
    if (!w) return null;
    var selEl = w.querySelector('[data-blouse-select]');
    var id = selEl && selEl.value ? selEl.value : null;
    if (!id) return null;
    var front = w.querySelector('[data-blouse-front-design]:checked');
    var back = w.querySelector('[data-blouse-back-design]:checked');
    var hintEl = w.querySelector('[data-blouse-hint]');
    return {
      id: id,
      front: front ? front.value : '',
      back: back ? back.value : '',
      hint: hintEl && hintEl.value ? hintEl.value.trim() : ''
    };
  }

  function resolveProductId(form) {
    if (form && form.dataset && form.dataset.productId) return form.dataset.productId;
    var block = document.querySelector('[data-product-addons]');
    return block ? block.getAttribute('data-product-id') : null;
  }

  function hasAddons(productId) {
    return selectedAddons(productId).length > 0 || !!predrapeVariant(productId) || !!blouseData(productId);
  }

  function clearCart() {
    return fetch(root + 'cart/clear.js', {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    }).then(function (res) {
      if (!res.ok) return res.text().then(function (t) { throw new Error('cart/clear failed: ' + res.status + ' ' + t); });
      return res.json();
    });
  }

  function postAdd(body) {
    return fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      body: body
    }).then(function (res) {
      if (!res.ok) return res.text().then(function (t) { throw new Error('cart/add failed: ' + res.status + ' ' + t); });
      return res.json();
    });
  }

  function addonBody(id, qty, parentTitle, props) {
    var f = new FormData();
    f.append('form_type', 'product');
    f.append('utf8', '✓');
    f.append('id', id);
    f.append('quantity', qty || 1);
    if (parentTitle) f.append('properties[Added with]', parentTitle);
    if (props) {
      Object.keys(props).forEach(function (k) {
        if (props[k]) f.append('properties[' + k + ']', props[k]);
      });
    }
    return f;
  }

  // Open Razorpay Magic Checkout against the current cart (all line items,
  // including add-ons, are honoured). Called via JS rather than through the
  // native Shopify payment button, so Razorpay's SSO login hooks never fire
  // and the user lands directly on the Magic Checkout form. Falls back to the
  // cart page if the Razorpay API is unavailable.
  function proceedToMagicCheckout(skin) {
    if (skin) {
      skin.classList.remove('is-loading');
      skin.__buynowBusy = false;
    }
    if (typeof window.openRzpCheckout === 'function') {
      try {
        window.openRzpCheckout({}, { sidecart: true });
        return;
      } catch (e) {
        console.error('[BuyNow+Addons] openRzpCheckout failed, falling back to cart', e);
      }
    }
    window.location.href = root + 'cart';
  }

  function runBuyNowWithAddons(form, productId, skin) {
    if (skin.__buynowBusy) return;
    skin.__buynowBusy = true;
    skin.classList.add('is-loading');

    var parentTitle = (form.dataset && form.dataset.productTitle) || '';

    // Main line item: everything inside the product form (id, quantity, in-form properties)...
    var mainBody = new FormData(form);
    // ...plus custom line-item property fields rendered outside the form (mirrors theme.js actionAddToCart2).
    document.querySelectorAll('.line-item-property__field [name^="properties"]').forEach(function (p) {
      if (p.value == null) return;
      if (p.type === 'file') { if (p.files[0]) mainBody.append(p.name, p.files[0]); }
      else mainBody.append(p.name, p.value);
    });

    var chain = clearCart().then(function () { return postAdd(mainBody); });

    selectedAddons(productId).forEach(function (a) {
      chain = chain.then(function () { return postAdd(addonBody(a.id, a.quantity, parentTitle)); });
    });

    var pd = predrapeVariant(productId);
    if (pd) chain = chain.then(function () { return postAdd(addonBody(pd, 1, parentTitle)); });

    var bl = blouseData(productId);
    if (bl) {
      chain = chain.then(function () {
        return postAdd(addonBody(bl.id, 1, parentTitle, {
          'Blouse Front Design': bl.front,
          'Blouse Back Design': bl.back,
          'Design Hint': bl.hint
        }));
      });
    }

    chain
      .then(function () { proceedToMagicCheckout(skin); })
      .catch(function (err) {
        console.error('[BuyNow+Addons]', err);
        skin.__buynowBusy = false;
        skin.classList.remove('is-loading');
        alert('Sorry, we could not start checkout with your add-ons. Please use "Add to Cart" instead.');
      });
  }

  function ensureGuard(skin) {
    if (skin.__guard) return;
    // The overlay must reliably sit above the Shopify payment button (z-index 3).
    // Styles are set inline so the feature does not depend on an external CSS
    // asset being deployed.
    if (getComputedStyle(skin).position === 'static') skin.style.position = 'relative';
    var overlay = document.createElement('div');
    overlay.className = 'rzp-buynow-guard';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.style.cssText = 'position:absolute;inset:0;top:0;left:0;right:0;bottom:0;z-index:5;cursor:pointer;display:none;';
    skin.appendChild(overlay);
    skin.__guard = overlay;
    overlay.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      var form = skin.closest('form');
      if (!form) return;
      runBuyNowWithAddons(form, resolveProductId(form), skin);
    });
  }

  // Always show the overlay so all BUY NOW clicks go straight to /checkout
  // without triggering the Razorpay SSO login prompt.
  function refresh() {
    var skins = document.querySelectorAll('.rzp-buynow-skin');
    Array.prototype.forEach.call(skins, function (skin) {
      var form = skin.closest('form');
      if (!form) return;
      ensureGuard(skin);
      var on = hasAddons(resolveProductId(form));
      skin.classList.toggle('has-addons-selected', on);
      skin.__guard.style.display = 'block';
    });
  }

  function init() { refresh(); }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);

  // Recompute whenever add-on selections change.
  document.addEventListener('change', refresh, true);
  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('[data-blouse-toggle],[data-addons-toggle],[data-predrape-toggle]')) {
      setTimeout(refresh, 0);
    }
  }, true);
  // Theme editor / dynamic sections.
  document.addEventListener('shopify:section:load', function () { setTimeout(refresh, 50); });
})();
